import React, { memo, useCallback, useMemo, useState } from "react";
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import { ColumnsType, ColumnType, TableProps } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { Table, Input, Space, message, Alert, Button, Popconfirm } from "antd";
import { FilteredPagedModel, EntityBase } from "../entities/common";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faEdit, faEye, faTrash, faFilter, faCheck, faTimes, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { EditableCell, EditableRow } from "./dataTableEditingComponents";
import { FilterDropdownProps, SorterResult } from "antd/lib/table/interface";
import { DateTimeViewer } from "./dateTimeViewer";
import { ExlExpCellType, ExlExpDataType, ExportToExcel } from "./ExportToExcel";
import dayjs from "dayjs";

type DataTableNavigationActionType = "view" | "logs" | "edit";
type DataTablePresenterType = {
  [field: string]: (value: any, record: any) => string | number | React.ReactNode;
};
type DataTableExportPresenterType = {
  [field: string]: (value: any, record: any) => string | number;
};

export type DataTableFieldType =
  | string
  | {
      name: string;
      editable?: boolean;
      title?: string;
      sorter?: boolean;
      filter?: "search" | ((selectedKeys: React.Key[], setSelectedKeys: (selectedKeys: React.Key[]) => void) => React.ReactNode);
      filterArg?: string;
      type?: "string" | "boolean" | "date" | "datetime";
      width?: number;
    };
export interface DataTableProps extends Omit<TableProps<any>, "title"> {
  query: Function;
  queryArgs?: { [x: string]: any };
  defaultQueryArgs?: { [x: string]: any };
  fields: Array<DataTableFieldType>;
  presenter?: DataTablePresenterType;
  exportPresenter?: DataTableExportPresenterType;
  title?: string | React.ReactNode;
  exportTitle?: string;
  navigationActions?: DataTableNavigationActionType[] | ((record: any) => DataTableNavigationActionType[]);
  customActions?:
    | ((record: any, i: number, state: any, navigate: NavigateFunction) => React.ReactNode)[]
    | ((record: any) => ((record: any, i: number, state: any, navigate: NavigateFunction) => React.ReactNode)[]);
  hasRowSelection?: boolean;
  deleteCallback?: (id: number | string) => Promise<boolean>;
  multiDeleteCallback?: (ids: number[] | string[]) => Promise<boolean>;
  editCallback?: (record: any) => Promise<boolean>;
  filterQueryData?: (data: any) => any;
  customHeaderFilter?: React.ReactNode;
}

export const DataTable = memo(
  ({
    query,
    queryArgs,
    defaultQueryArgs,
    fields,
    presenter,
    exportPresenter,
    title,
    exportTitle,
    navigationActions,
    customActions,
    deleteCallback,
    multiDeleteCallback,
    editCallback,
    hasRowSelection = true,
    filterQueryData,
    customHeaderFilter,
    ...others
  }: DataTableProps) => {
    let location = useLocation();
    const { t } = useTranslation();
    const navigate = useNavigate();

    let previousQueryArg = (location.state as any)?.queryArg;
    const [page, setPage] = useState(previousQueryArg?.page ?? 1);
    const [pageSize, setPageSize] = useState(previousQueryArg?.pageSize ?? 10);
    const [sortFieldName, setSortFieldName] = useState(defaultQueryArgs?.sortFieldName || "");
    const [sortOrderDescending, setSortOrderDescending] = useState(defaultQueryArgs?.descending || false);
    const [searchTerm, setsearchTerm] = useState(previousQueryArg?.searchTerm ?? "");
    const [filter, setFilter] = useState(previousQueryArg?.searchTerm ?? "");
    const [columnSearchFilters, setColumnSearchFilters] = useState<{
      [x: string]: string;
    }>({});

    const _queryArgs = useMemo<FilteredPagedModel>(
      () => ({
        ...previousQueryArg,
        page,
        pageSize,
        searchTerm,
        sortFieldName,
        descending: sortOrderDescending,
        ...columnSearchFilters,
        ...queryArgs,
      }),
      [columnSearchFilters, page, pageSize, previousQueryArg, queryArgs, searchTerm, sortFieldName, sortOrderDescending],
    );

    // useEffect(() => {
    //   // Reset page when any search params changed
    //   console.log("reset page");
    //   setPage(1);
    // }, [searchTerm]);

    const { data: queryResult, isLoading, isFetching, isError, refetch } = query(_queryArgs);

    const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);

    const deleteRecord = useCallback(
      async (id: number | string) => {
        if (!deleteCallback) return;

        message.loading({
          content: "در حال حذف رکورد",
          key: "deleteRecord",
          duration: 0,
        });

        const isSuccess = await deleteCallback(id);

        if (isSuccess) {
          message.success({
            content: "رکورد با موفقیت حذف شد",
            key: "deleteRecord",
            duration: 5,
          });
        } else {
          message.error({
            content: "حذف رکورد با خطا مواجه شد",
            key: "deleteRecord",
            duration: 5,
          });
        }
      },
      [deleteCallback],
    );

    const deleteRecords = useCallback(async () => {
      if (!multiDeleteCallback) return;

      message.loading({
        content: "در حال حذف رکوردهای انتخاب شده",
        key: "deleteRecords",
        duration: 0,
      });

      const isSuccess = await multiDeleteCallback(selectedRowKeys);

      if (isSuccess) {
        message.success({
          content: "رکوردهای انتخاب شده با موفقیت حذف شدند",
          key: "deleteRecords",
          duration: 5,
        });

        setSelectedRowKeys([]);
      } else {
        message.error({
          content: "حذف رکوردهای انتخاب شده با خطا مواجه شد",
          key: "deleteRecords",
          duration: 5,
        });
      }
    }, [multiDeleteCallback, selectedRowKeys]);

    const editRecord = useCallback(
      async (record: any) => {
        if (!editCallback) return;

        message.loading({
          content: "در حال ثبت تغییرات",
          key: "editRecord",
          duration: 0,
        });

        const isSuccess = await editCallback(record);

        if (isSuccess) {
          message.success({
            content: "تغییرات با موفقیت ثبت شد",
            key: "editRecord",
            duration: 5,
          });
        } else {
          message.error({
            content: "ثبت تغییرات با خطا مواجه شد",
            key: "editRecord",
            duration: 5,
          });
        }
      },
      [editCallback],
    );

    const viewRecordDetail = useCallback(
      (record: EntityBase) => {
        navigate(`${record.id}`, { state: { queryArg: _queryArgs, record } });
      },
      [_queryArgs, navigate],
    );

    const gotoEditPage = useCallback(
      (record: EntityBase) => {
        navigate(`${record.id}/edit`, { state: { queryArg: _queryArgs, record } });
      },
      [_queryArgs, navigate],
    );

    const viewRecordLogs = useCallback(
      (record: EntityBase) => {
        navigate(`${record.id}/logs`, { state: { queryArg: _queryArgs, record } });
      },
      [_queryArgs, navigate],
    );

    const handleColumnSearch = useCallback(
      (selectedKeys: React.Key[], confirm: Function, dataIndex: string, filterArg?: string) => {
        confirm();
        setColumnSearchFilters({
          ...columnSearchFilters,
          [filterArg || dataIndex]: selectedKeys[0]?.toString() ?? "",
        });

        setPage(1);
      },
      [columnSearchFilters],
    );

    const handleColumnSearchReset = useCallback((clearFilters?: () => void) => {
      if (!clearFilters) return;

      clearFilters();
    }, []);

    const getColumnSearchProps = useCallback(
      (dataIndex: string, field: any) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
          <div className="p-2">
            <div className="mb-2">
              {typeof field.filter === "function" ? (
                field.filter(selectedKeys, setSelectedKeys)
              ) : (
                <Input
                  placeholder={`جستجوی ${t(dataIndex)}`}
                  value={selectedKeys[0] as any}
                  onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                  onPressEnter={() => handleColumnSearch(selectedKeys, confirm, dataIndex, field.filterArg)}
                />
              )}
            </div>
            <Space>
              <Button
                type="primary"
                onClick={() => handleColumnSearch(selectedKeys, confirm, dataIndex, field.filterArg)}
                icon={<FontAwesomeIcon size="xs" className="anticon" icon={faFilter} />}
                size="small"
                style={{ width: 90 }}>
                فیلتر
              </Button>
              <Button onClick={() => handleColumnSearchReset(clearFilters)} size="small" style={{ width: 90 }}>
                بازنشانی
              </Button>
            </Space>
          </div>
        ),
      }),
      [handleColumnSearch, handleColumnSearchReset, t],
    );

    const columns = useMemo<ColumnsType<EntityBase>>(() => {
      let cols = fields.map((field) => {
        let column: ColumnType<EntityBase> = {};
        let fieldName: string;

        if (typeof field === "string") {
          fieldName = field;

          column.title = t(field);
          column.dataIndex = field;
          column.key = field;
        } else {
          fieldName = field.name;

          column.title = field.title || t(field.name);
          column.dataIndex = field.name;
          column.key = field.name;
          column.sorter = field.sorter;
          column.width = field.width;

          if (field.editable && editCallback) {
            column.onCell = (record: EntityBase) => ({
              record,
              editable: field.editable,
              dataIndex: column.dataIndex?.toString(),
              title: column.title?.toString(),
              handleSave: editRecord,
            });
          }

          if (field.filter) {
            column = {
              ...column,
              ...getColumnSearchProps(column.dataIndex, field),
            };
          }

          if (field?.type === "boolean") {
            column.render = (value: boolean) => (
              <FontAwesomeIcon icon={value ? faCheck : faTimes} className={value ? "text-green-500" : "text-red-500"} />
            );
          } else if (field.type === "date") {
            column.render = (value: any) => <DateTimeViewer value={value} mode="date" />;
          } else if (field.type === "datetime") {
            column.render = (value: any) => <DateTimeViewer value={value} mode="datetime" />;
          }
        }

        if (presenter && presenter.hasOwnProperty(fieldName)) {
          column.render = (value: any, record: any) => presenter[fieldName](value, record);
        }

        return column;
      });

      if (navigationActions || deleteCallback || customActions) {
        cols.push({
          title: "",
          key: "actions",
          align: "center",
          render: (value, record) => {
            const navActions = typeof navigationActions === "function" ? navigationActions(record) : navigationActions;

            return (
              <div className="flex flex-wrap gap-1">
                {navActions?.includes("view") && (
                  <Button title="جزییات" size="small" icon={<FontAwesomeIcon icon={faEye} />} onClick={() => viewRecordDetail(record)}></Button>
                )}
                {navActions?.includes("logs") && (
                  <Button title="سوابق" size="small" icon={<FontAwesomeIcon icon={faClock} />} onClick={() => viewRecordLogs(record)}></Button>
                )}
                {navActions?.includes("edit") && (
                  <Button
                    title="ویرایش"
                    size="small"
                    type="primary"
                    icon={<FontAwesomeIcon icon={faEdit} />}
                    onClick={() => gotoEditPage(record)}></Button>
                )}
                {deleteCallback && (
                  <Popconfirm
                    color="#ffcdd2"
                    placement="topRight"
                    title="آیا اطمینان دارید؟"
                    okText="حذف"
                    cancelText="لغو"
                    okButtonProps={{ danger: true }}
                    icon={<FontAwesomeIcon className="text-2xl text-red-600" icon={faExclamationTriangle} />}
                    onConfirm={() => deleteRecord(record.id)}>
                    <Button title="حذف" size="small" danger icon={<FontAwesomeIcon icon={faTrash} />}></Button>
                  </Popconfirm>
                )}

                {customActions && typeof customActions === "object" && customActions.map((x, i) => x(record, i, { queryArg: _queryArgs }, navigate))}
                {customActions &&
                  typeof customActions === "function" &&
                  customActions(record).map((x, i) => x(record, i, { queryArg: _queryArgs }, navigate))}
              </div>
            );
          },
          width: Math.min(10 + ((navigationActions?.length ?? 0) + (deleteCallback ? 1 : 0) + (customActions?.length ?? 0)) * 45, 120),
        });
      }

      return cols;
    }, [
      _queryArgs,
      customActions,
      deleteCallback,
      deleteRecord,
      editCallback,
      editRecord,
      fields,
      getColumnSearchProps,
      gotoEditPage,
      navigate,
      navigationActions,
      presenter,
      t,
      viewRecordDetail,
      viewRecordLogs,
    ]);

    const components = useMemo(
      () => ({
        body: {
          row: EditableRow,
          cell: EditableCell,
        },
      }),
      [],
    );

    const onPageOrPageSizeChange = useCallback((current: number, size: number) => {
      setPage(current);
      setPageSize(size);
    }, []);

    const showTotalFormat = useCallback((total: number) => {
      return `(تعداد کل: ${total})`;
    }, []);

    const onSelectedRowsChange = useCallback((keys: any[]) => {
      setSelectedRowKeys(keys);
    }, []);

    const rowSelection = useMemo(
      () => ({
        selectedRowKeys,
        onChange: onSelectedRowsChange,
      }),
      [onSelectedRowsChange, selectedRowKeys],
    );

    const handleTableChange = useCallback(
      (pagination: any, filters: any, sorter: any) => {
        let sorterResult = sorter as SorterResult<EntityBase>;
        setSortFieldName(sorterResult.field?.toString() ?? defaultQueryArgs?.sortFieldName ?? "");
        setSortOrderDescending(sorterResult.order ? sorterResult.order === "descend" : defaultQueryArgs?.descending);
      },
      [defaultQueryArgs],
    );

    const tableData = useMemo(
      () => (queryResult?.data ? (filterQueryData ? filterQueryData(queryResult.data) : queryResult.data) : null),
      [filterQueryData, queryResult],
    );

    const excelExportData = useCallback(() => {
      const cols = columns?.filter((c) => c.key !== "actions");
      const data: ExlExpDataType = {
        theader: [cols?.map((c) => ({ value: c.title })) as ExlExpCellType[]],
        tbody: tableData?.map((row: any) => ({
          cells: cols?.map((col: any) => ({
            value:
              exportPresenter && exportPresenter.hasOwnProperty(col.dataIndex)
                ? exportPresenter[col.dataIndex](row[col.dataIndex], row)
                : row[col.dataIndex],
          })),
        })),
        columns: cols?.map((col) => ({ width: 30 })),
      };

      return data;
    }, [columns, exportPresenter, tableData]);

    if (isError) {
      return (
        <Alert
          message="خطا"
          showIcon
          description="بارگزاری اطلاعات با خطا مواجه شد"
          type="error"
          action={
            <Button size="small" danger onClick={() => refetch()}>
              تلاش مجدد
            </Button>
          }
        />
      );
    }

    return (
      <Table
        title={() => (
          <div>
            <div className="flex flex-col lg:flex-row lg:justify-between mb-2 gap-2">
              <div className="flex-1 flex gap-4">
                {title && typeof title === "string" && <h4>{title}</h4>}
                {customHeaderFilter}
              </div>
              <div className="flex self-end">
                {multiDeleteCallback && selectedRowKeys.length > 0 && (
                  <Popconfirm
                    color="#ffedd5"
                    placement="topRight"
                    title="آیا اطمینان دارید؟"
                    okText="حذف"
                    cancelText="لغو"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => deleteRecords()}>
                    <Button
                      size="small"
                      title="حذف رکورد های انتخاب شده"
                      type="dashed"
                      danger
                      icon={<FontAwesomeIcon className="anticon" icon={faTrash} />}>
                      حذف انتخاب شده ها
                    </Button>
                  </Popconfirm>
                )}
                <div>
                  <ExportToExcel
                    title={exportTitle ?? `table_${dayjs().format("YYYYMMDD")}`}
                    data={excelExportData}
                    header={{ value: exportTitle ?? "", span: fields.length }}
                    freeze={{ xSplit: 1, ySplit: 3 }}
                  />
                  <Input.Search
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    onSearch={() => {
                      setsearchTerm(filter);
                      setPage(1);
                    }}
                    placeholder="فیلتر..."
                    loading={false}
                    className="md:w-64 w-full mr-2"
                    size="small"
                  />
                </div>
              </div>
            </div>
            {React.isValidElement(title) && title}
          </div>
        )}
        dataSource={tableData}
        components={components}
        rowKey="id"
        rowClassName={() => "editable-row"}
        loading={isLoading || isFetching}
        columns={columns}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: queryResult?.totalCount ?? 0,
          showTotal: showTotalFormat,
          onChange: onPageOrPageSizeChange,
          pageSizeOptions: [10, 20, 50, 100, 1000, 10000, 100000, 1000000],
          showSizeChanger: true,
        }}
        bordered
        scroll={{ y: "max(calc(100vh - 430px), 150px)", x: columns.length * 100 }}
        onRow={(record) => ({
          onDoubleClick: () =>
            (typeof navigationActions === "function" ? navigationActions(record) : navigationActions)?.includes("view")
              ? viewRecordDetail(record)
              : undefined,
        })}
        rowSelection={hasRowSelection ? rowSelection : undefined}
        onChange={handleTableChange}
        {...others}
      />
    );
  },
);

DataTable.displayName = "Data table";
